/**
 * Linienzeichen: das amtliche Produktzeichen für Züge, ein blaues Kästchen
 * für alles, was auf Strasse, Wasser oder am Berg fährt.
 *
 * Die SBB veröffentlicht ihre Produktzeichen einzeln als SVG - und zwar je
 * Linie, nicht nur je Gattung: `ir-70.svg` trägt die Nummer schon im Zeichen.
 * Gemessen: alle 59 x 20 px, rund 1 KB, `access-control-allow-origin: *`,
 * `cache-control: max-age=315360000`. Also abrufbar, einheitlich hoch und
 * nach dem ersten Mal aus dem Browsercache.
 *
 * Es gibt sie aber nicht für jede Kombination - `ec-1` und `ice-278` fehlen,
 * `ec` und `ice` sind da. Deshalb drei Stufen: Gattung mit Nummer, dann die
 * Gattung allein, dann das eigene Kästchen. Gezeichnet wird immer zuerst das
 * eigene; das amtliche tritt an seine Stelle, sobald es geladen ist. So steht
 * nie ein kaputtes Bild im Fahrplan, auch wenn der Dienst ausfällt.
 */
const SBB = 'https://icons.app.sbb.ch/icons';

/**
 * Was als Zug gilt. Nur diese Gattungen fragen beim Zeichendienst nach -
 * alles andere bekommt ohne Umweg das blaue Kästchen, auch Unbekanntes.
 */
const ZUG = new Set([
  'IC', 'ICE', 'ICN', 'IR', 'IRE', 'RE', 'R', 'S', 'SN', 'PE', 'VAE',
  'EC', 'ECE', 'EN', 'NJ', 'RJ', 'RJX', 'TGV', 'TER', 'D', 'EXT',
  'ARC', 'AVE', 'ATZ', 'GEX', 'BEX', 'WB', 'MGB', 'RB', 'ZUG'
]);

const istZug = (kat) => ZUG.has(kat.toUpperCase());

/**
 * Liniennummer von Fahrtnummer trennen.
 *
 * Der Fahrplan füllt `number` nicht einheitlich: meist steht dort die Linie
 * ("70", "26", "N1"), manchmal aber die Fahrtnummer ("002344"). Eine
 * Liniennummer ist im Schweizer Fahrplan nie länger als drei Zeichen - was
 * länger ist, gehört nicht aufs Zeichen.
 */
function liniennummer(nummer) {
  const n = String(nummer || '').trim();
  return n.length <= 3 ? n : '';
}

/** Einmal je Dateiname fragen - eine Abfahrtstafel zeigt Linien doppelt. */
const geprueft = new Map();

function vorhanden(name) {
  if (geprueft.has(name)) return geprueft.get(name);
  const p = new Promise((fertig) => {
    const bild = new Image();
    bild.onload = () => fertig(true);
    bild.onerror = () => fertig(false);
    bild.src = `${SBB}/${name}.svg`;
  });
  geprueft.set(name, p);
  return p;
}

function kaestchen(kat, num, zug) {
  const el = document.createElement('span');
  el.className = `lz lz--${zug ? 'zug' : 'bus'}`;
  // Beim Bus steht die Nummer drin, so wie sie vorn am Fahrzeug steht. Beim
  // Zug braucht es die Gattung dazu - "70" allein sagt niemandem etwas.
  el.textContent = zug ? `${kat}${num ? ' ' + num : ''}`.trim() : (num || kat);
  el.title = `${kat}${num ? ' ' + num : ''}`.trim();
  return el;
}

/**
 * Zeichen für eine Linie. Gibt sofort ein Element zurück; bei Zügen wird es
 * später gegen das amtliche Zeichen getauscht, falls es eines gibt.
 */
export function linienZeichen(kategorie, nummer) {
  const kat = String(kategorie || '').trim();
  const num = liniennummer(nummer);
  const zug = istZug(kat);
  const el = kaestchen(kat || '?', num, zug);
  if (!kat || !zug) return el;

  const namen = [num ? `${kat}-${num}` : null, kat]
    .filter(Boolean)
    .map((n) => n.toLowerCase().replace(/[^a-z0-9-]/g, ''));

  (async () => {
    for (const name of namen) {
      if (!(await vorhanden(name))) continue;
      // Inzwischen neu gezeichnet? Dann hängt das Element nirgends mehr.
      if (!el.parentNode) return;
      const img = document.createElement('img');
      img.className = 'lz lz--sbb';
      img.src = `${SBB}/${name}.svg`;
      img.alt = el.title;
      img.title = el.title;
      img.referrerPolicy = 'no-referrer';
      el.replaceWith(img);
      return;
    }
  })();

  return el;
}

/** Dasselbe aus einem fertigen Text wie "IR 70" oder "B 605". */
export function linienZeichenAus(linie) {
  const m = /^([A-Za-zÄÖÜäöü]+)\s*(.*)$/.exec(String(linie || '').trim());
  return m ? linienZeichen(m[1], m[2]) : kaestchen(String(linie || '?'), '', false);
}
